import React, { useState, useEffect } from 'react';
import { Check, Mail, Zap, Youtube, Twitter, Instagram, Twitch, Disc, Unlock, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PLATFORMS = [
    { name: 'YouTube', icon: Youtube },
    { name: 'Telegram', icon: Zap }, 
    { name: 'Discord', icon: Disc },
    { name: 'X', icon: Twitter },
    { name: 'Instagram', icon: Instagram },
    { name: 'Spotify', icon: Zap },
    { name: 'Twitch', icon: Twitch },
    { name: 'TikTok', icon: Zap },
];

const INITIAL_EMAILS = [
    { email: 'a***n@gmail.com', tag: 'VIP', color: 'bg-yellow-500/20 text-yellow-500', time: 'just now', id: 1 },
    { email: 's***h@yahoo.com', tag: 'Newsletter', color: 'bg-blue-500/20 text-blue-400', time: '2m ago', id: 2 },
    { email: 'm***r@hotmail.com', tag: 'Preset Buyers', color: 'bg-purple-500/20 text-purple-400', time: '15m ago', id: 3 },
];

const MOCK_EMAILS = [
    'j***n@gmail.com', 'l***a@yahoo.com', 'd***v@hotmail.com', 'e***a@gmail.com',
    'p***r@outlook.com', 'c***s@gmail.com', 'k***e@yahoo.com', 'm***x@gmail.com'
];

const TAGS = [
    { tag: 'VIP', color: 'bg-yellow-500/20 text-yellow-500' },
    { tag: 'Newsletter', color: 'bg-blue-500/20 text-blue-400' },
    { tag: 'New Fan', color: 'bg-green-500/20 text-green-400' },
    { tag: 'Preset Buyers', color: 'bg-purple-500/20 text-purple-400' },
];

const FeaturesSection = () => {
    
    const [stats, setStats] = useState({ us: 42, uk: 28, in: 15 });

    const [unlockSteps, setUnlockSteps] = useState([
        { text: 'Subscribe on YouTube', done: false },
        { text: 'Join Telegram Channel', done: false },
        { text: 'Follow on X', done: false },
    ]);
    const [isUnlocked, setIsUnlocked] = useState(false);

    const [subCount, setSubCount] = useState(8245);
    const [recentEmails, setRecentEmails] = useState(INITIAL_EMAILS);

    useEffect(() => {
        const interval = setInterval(() => {
            setStats(prev => {
                const shift = (Math.random() - 0.5) * 4; 
                const newUs = Math.min(Math.max(prev.us + shift, 35), 50);
                const newUk = Math.min(Math.max(prev.uk - (shift * 0.6), 20), 35);
                const newIn = Math.min(Math.max(prev.in - (shift * 0.4), 10), 25);
                return { us: newUs, uk: newUk, in: newIn };
            });
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        
        let isCancelled = false;

        const runSequence = async () => {
            if (isCancelled) return;

            setUnlockSteps(steps => steps.map(s => ({ ...s, done: false })));
            setIsUnlocked(false);

            await new Promise(r => setTimeout(r, 1500));
            if (isCancelled) return;
            setUnlockSteps(steps => [ { ...steps[0], done: true }, steps[1], steps[2] ]);

            await new Promise(r => setTimeout(r, 1500));
            if (isCancelled) return;
            setUnlockSteps(steps => [ steps[0], { ...steps[1], done: true }, steps[2] ]);

            await new Promise(r => setTimeout(r, 1500));
            if (isCancelled) return;
            setUnlockSteps(steps => [ steps[0], steps[1], { ...steps[2], done: true } ]);

            await new Promise(r => setTimeout(r, 500));
            if (isCancelled) return;
            setIsUnlocked(true);

            await new Promise(r => setTimeout(r, 3000));
            if (!isCancelled) {
                runSequence();
            }
        };

        runSequence();
        return () => { isCancelled = true; };
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            
            if (Math.random() > 0.3) {
                setSubCount(prev => prev + Math.floor(Math.random() * 3) + 1);
                
                const newEmail = MOCK_EMAILS[Math.floor(Math.random() * MOCK_EMAILS.length)];
                const randomTag = TAGS[Math.floor(Math.random() * TAGS.length)];
                
                const newItem = {
                    email: newEmail,
                    tag: randomTag.tag,
                    color: randomTag.color,
                    time: 'just now',
                    id: Date.now()
                };

                setRecentEmails(prev => {
                    const updated = [newItem, ...prev].slice(0, 3);
                    
                    return updated.map((item, index) => {
                        if (index === 1 && item.time === 'just now') return { ...item, time: '1m ago' };
                        if (index === 2 && item.time === '1m ago') return { ...item, time: '3m ago' };
                        return item;
                    });
                });
            }
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const completedSteps = unlockSteps.filter(s => s.done).length;
    const progress = (completedSteps / unlockSteps.length) * 100;

    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-24 z-10 relative overflow-hidden">
            {}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-32 relative"
            >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
                    Trusted by <span className="text-purple-500">100K+</span> creators who gained <span className="text-purple-500">500M+</span> followers
                </h2>
                
                {}
                <div className="w-full overflow-hidden relative flex items-center">
                    {}
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#050505] to-transparent z-10"></div>
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#050505] to-transparent z-10"></div>
                    
                    <motion.div 
                        className="flex items-center gap-16 whitespace-nowrap opacity-50 grayscale hover:grayscale-0 transition-all duration-500"
                        animate={{ x: [0, -1000] }}
                        transition={{ 
                            repeat: Infinity, 
                            duration: 20, 
                            ease: "linear" 
                        }}
                    >
                        {}
                        {[...PLATFORMS, ...PLATFORMS].map((platform, i) => (
                            <div key={i} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                                <platform.icon size={28} />
                                <span className="text-2xl font-bold tracking-tight">{platform.name}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </motion.div>

            {}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {}
                <motion.div 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-col justify-center p-8 lg:p-12 bg-[#111111]/50 border border-white/5 rounded-3xl backdrop-blur-sm shadow-[0_0_40px_rgba(0,0,0,0.5)]"
                >
                    <div className="text-purple-500 font-semibold tracking-wider text-sm mb-4 uppercase">Growth Actions</div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">Multiply your audience automatically</h3>
                    <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                        Set the requirements for unlocking your premium content. Whether it's subscribing to your YouTube channel, joining your Telegram group, or following you on X, Linkey ensures they take action before they get access.
                    </p>
                    <button className="self-start px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors shadow-[0_0_20px_rgba(168,85,247,0.3)]">
                        Start Growing Now
                    </button>
                </motion.div>
                
                {/* Visual Content - Link Lock Card Mockup */}
                <motion.div 
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="p-8 bg-[#0a0a0a] border border-white/5 rounded-3xl flex items-center justify-center relative overflow-hidden h-[450px]"
                >
                    {/* Background glow */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/20 rounded-full blur-[100px]"></div>
                    
                    {/* Mock Card */}
                    <div className="relative w-full max-w-sm bg-[#151515] border border-white/10 rounded-2xl p-6 shadow-2xl transition-all duration-500" style={{ transform: isUnlocked ? 'scale(1.02)' : 'scale(1)' }}>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                                <Zap className="text-purple-400" size={24} />
                            </div>
                            <div>
                                <h4 className="text-white font-medium">Exclusive Preset Pack</h4>
                                <p className="text-gray-500 text-sm">Complete steps to unlock</p>
                            </div>
                        </div>

                        <div className="space-y-3 mb-6 relative">
                            <AnimatePresence>
                                {isUnlocked && (
                                    <motion.div 
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#151515]/90 backdrop-blur-sm rounded-xl border border-green-500/30"
                                    >
                                        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-3">
                                            <Unlock className="text-green-400" size={32} />
                                        </div>
                                        <div className="text-white font-bold mb-3">Link Unlocked!</div>
                                        <button className="flex items-center gap-2 px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors text-sm">
                                            Go to Link <ArrowRight size={16} />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {unlockSteps.map((step, i) => (
                                <motion.div 
                                    key={i} 
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 cursor-pointer ${step.done ? 'border-purple-500/30 bg-purple-500/10' : 'border-white/5 bg-white/5 hover:bg-white/10'}`}
                                >
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-300 ${step.done ? 'bg-purple-500' : 'border border-gray-600'}`}>
                                        {step.done && <Check size={12} className="text-white" />}
                                    </div>
                                    <span className={step.done ? 'text-white' : 'text-gray-400'}>{step.text}</span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between text-xs font-medium">
                                <span className="text-gray-400">Unlock progress</span>
                                <span className="text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded-full">{completedSteps}/{unlockSteps.length} steps</span>
                            </div>
                            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                <motion.div 
                                    className="bg-purple-500 h-2 rounded-full" 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Two Column Grid for Analytics and Email */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Analytics */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-[#111111]/50 border border-white/5 rounded-3xl p-8 lg:p-12 flex flex-col h-full backdrop-blur-sm shadow-[0_0_40px_rgba(0,0,0,0.5)]"
                >
                    <div className="mb-10 flex-grow">
                        <div className="text-purple-500 font-semibold tracking-wider text-sm mb-4 uppercase">Analytics</div>
                        <h3 className="text-2xl font-bold text-white mb-4">Real-time audience insights</h3>
                        <p className="text-gray-400">
                            See exactly where your audience comes from, which platforms convert best, and monitor live traffic updates as your campaigns grow.
                        </p>
                    </div>
                    
                    {/* Mock Analytics UI */}
                    <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
                        <div className="mb-6 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                                <span className="text-xs text-white font-medium uppercase tracking-wider">Live Traffic</span>
                            </div>
                            <div className="flex gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                                <div className="w-1.5 h-1.5 rounded-full bg-gray-700"></div>
                                <div className="w-1.5 h-1.5 rounded-full bg-gray-700"></div>
                            </div>
                        </div>
                        
                        <div className="space-y-5">
                            <div>
                                <div className="flex justify-between text-sm mb-2 font-medium">
                                    <span className="text-gray-300">United States</span>
                                    <span className="text-gray-500 font-mono">{stats.us.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                                    <motion.div 
                                        className="bg-purple-500 h-1.5 rounded-full" 
                                        animate={{ width: `${stats.us}%` }}
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2 font-medium">
                                    <span className="text-gray-300">United Kingdom</span>
                                    <span className="text-gray-500 font-mono">{stats.uk.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                                    <motion.div 
                                        className="bg-purple-400 h-1.5 rounded-full" 
                                        animate={{ width: `${stats.uk}%` }}
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2 font-medium">
                                    <span className="text-gray-300">India</span>
                                    <span className="text-gray-500 font-mono">{stats.in.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                                    <motion.div 
                                        className="bg-purple-300 h-1.5 rounded-full" 
                                        animate={{ width: `${stats.in}%` }}
                                        transition={{ duration: 1, ease: "easeInOut" }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Email Capturing */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-[#111111]/50 border border-white/5 rounded-3xl p-8 lg:p-12 flex flex-col h-full backdrop-blur-sm shadow-[0_0_40px_rgba(0,0,0,0.5)]"
                >
                    <div className="mb-10 flex-grow">
                        <div className="text-purple-500 font-semibold tracking-wider text-sm mb-4 uppercase">Lead Generation</div>
                        <h3 className="text-2xl font-bold text-white mb-4">Build an audience you own</h3>
                        <p className="text-gray-400">
                            Collect emails as part of the unlock process. Segment your most loyal fans and own your contact list independent of social algorithms.
                        </p>
                    </div>

                    {/* Mock Email UI */}
                    <div className="bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-4">
                            <div className="flex items-center gap-2 text-white font-medium">
                                <Mail size={16} className="text-gray-400" />
                                Subscribers
                            </div>
                            <motion.span 
                                key={subCount}
                                initial={{ scale: 1.2, color: '#a855f7' }}
                                animate={{ scale: 1, color: '#ffffff' }}
                                className="text-xl font-bold"
                            >
                                {subCount.toLocaleString()}
                            </motion.span>
                        </div>
                        
                        <div className="space-y-4 relative min-h-[140px]">
                            <AnimatePresence>
                                {recentEmails.map((item, i) => (
                                    <motion.div 
                                        key={item.id}
                                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                                        animate={{ opacity: 1, y: i * 48, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ duration: 0.3 }}
                                        className="flex items-center justify-between absolute w-full"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                            <span className="text-sm text-gray-300 font-mono">{item.email}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider ${item.color}`}>
                                                {item.tag}
                                            </span>
                                            <span className="text-xs text-gray-600 hidden sm:block w-12 text-right">{item.time}</span>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Integrations and Link-in-Bio */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <motion.div 
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-[#111111]/50 border border-white/5 rounded-3xl p-8 lg:p-12 backdrop-blur-sm shadow-[0_0_40px_rgba(0,0,0,0.5)]"
                >
                    <div className="text-purple-500 font-semibold tracking-wider text-sm mb-4 uppercase">Integrations</div>
                    <h3 className="text-2xl font-bold text-white mb-4">Works everywhere you do</h3>
                    <p className="text-gray-400 mb-12">
                        Linkey integrates with over 50+ platforms. From YouTube and TikTok to Telegram and Discord. Manage all your growth channels from a single, powerful dashboard.
                    </p>
                    
                    <div className="mt-8 pt-8 border-t border-white/5">
                        <div className="text-purple-500 font-semibold tracking-wider text-sm mb-4 uppercase">Link-in-Bio</div>
                        <h3 className="text-2xl font-bold text-white mb-4">Your entire ecosystem, in one link</h3>
                        <p className="text-gray-400">
                            Create a stunning profile page hosting all your unlocked content, social accounts, and links in one place. No extra tools required.
                        </p>
                    </div>
                </motion.div>

                {/* Mock Link-in-bio UI */}
                <motion.div 
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-[#0a0a0a] border border-white/5 rounded-3xl p-8 flex items-center justify-center relative overflow-hidden"
                >
                    <div className="absolute w-64 h-64 bg-purple-600/10 rounded-full blur-[100px]"></div>
                    
                    <div className="relative w-full max-w-[280px] bg-[#151515] border border-white/10 rounded-[2rem] p-6 shadow-2xl h-auto min-h-[420px] flex flex-col items-center">
                        <div className="w-12 h-1.5 bg-gray-800 rounded-full mb-6"></div>
                        
                        <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 p-1 mb-4">
                            <div className="w-full h-full bg-[#151515] rounded-full flex items-center justify-center overflow-hidden border-2 border-[#151515]">
                                {/* Dummy avatar */}
                                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                    <svg className="w-10 h-10 text-gray-500 mt-2" fill="currentColor" viewBox="0 0 24 24"><path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                </div>
                            </div>
                        </div>
                        
                        <h4 className="text-white font-bold text-lg">Alex Rivera</h4>
                        <p className="text-gray-500 text-sm mb-6">@arivera</p>

                        <div className="w-full space-y-3">
                            <motion.div 
                                whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.1)" }}
                                className="w-full py-3.5 px-4 bg-white/5 border border-white/5 rounded-xl flex items-center justify-center text-sm text-white font-medium transition-colors shadow-sm cursor-pointer"
                            >
                                Check out my new video
                            </motion.div>
                            <motion.div 
                                whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.1)" }}
                                className="w-full py-3.5 px-4 bg-white/5 border border-white/5 rounded-xl flex items-center justify-center text-sm text-white font-medium transition-colors shadow-sm cursor-pointer"
                            >
                                Free Drum Kit (Unlock)
                            </motion.div>
                            <motion.div 
                                whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.1)" }}
                                className="w-full py-3.5 px-4 bg-white/5 border border-white/5 rounded-xl flex items-center justify-center text-sm text-white font-medium transition-colors shadow-sm cursor-pointer"
                            >
                                Join Discord Community
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default FeaturesSection;
